import React from 'react';
import { useLang } from '../contexts/LanguageContext';

/**
 * AutoTranslate — recursively walks React children and runs every string
 * literal through tt() so the entire UI is translated without manual
 * wrapping. Also translates common attribute props (placeholder, title,
 * aria-label, alt) where present.
 *
 * Used at the top of <App /> so a single mount handles header, body and
 * footer of every page.
 */

// Props whose VALUES are user-visible text and should be translated.
const TEXT_PROPS = ['placeholder', 'title', 'aria-label', 'alt'];

// Element TYPES whose children are *not* user-visible text and must be
// passed through verbatim (e.g. <script>, <style>, <code>).
const SKIP_TYPES = new Set(['script', 'style', 'code', 'pre']);

const isText = (v) => typeof v === 'string';

const translateNode = (node, tt) => {
  if (node == null || typeof node === 'boolean') return node;

  // Plain text
  if (isText(node)) {
    const trimmed = node.trim();
    if (!trimmed) return node;
    // Skip purely numeric, single-char, or symbol-only strings
    if (/^[\d\s\W]+$/.test(trimmed)) return node;
    return tt(node);
  }

  if (typeof node === 'number') return node;

  if (Array.isArray(node)) return node.map((c, i) => {
    const translated = translateNode(c, tt);
    // Preserve keys if children already had them
    return translated;
  });

  if (React.isValidElement(node)) {
    // Skip whole subtree for non-translatable element types
    if (typeof node.type === 'string' && SKIP_TYPES.has(node.type)) return node;

    // Translate common text-bearing props
    let nextProps = null;
    for (const key of TEXT_PROPS) {
      const v = node.props[key];
      if (isText(v) && v.trim()) {
        if (!nextProps) nextProps = {};
        nextProps[key] = tt(v);
      }
    }

    const kids = node.props.children;
    if (kids != null) {
      const translatedKids = translateNode(kids, tt);
      if (nextProps || translatedKids !== kids) {
        return React.cloneElement(node, nextProps || {}, translatedKids);
      }
    } else if (nextProps) {
      return React.cloneElement(node, nextProps);
    }
    return node;
  }

  return node;
};

const AutoTranslate = ({ children }) => {
  const { tt, lang } = useLang();
  // Fast path — no translation needed in English mode
  if (lang === 'en') return <>{children}</>;
  return <>{translateNode(children, tt)}</>;
};

export default AutoTranslate;
